import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Spinner } from '@/shared/ui/spinner';
import { BUILD_ID } from '@/shared/lib/app-version';
import { beginPendingUpdate } from './update-flow';

/**
 * Wait until a different service worker controls this page.
 *
 * Resolves on `controllerchange`, or after `timeoutMs` so a page that will
 * never be claimed still reloads. The timeout is a safety net, not the happy
 * path: `clientsClaim` is off in `vite.config.ts`, so a page that was not
 * controlled when the new worker activated receives no `controllerchange` at
 * all — waiting forever there is precisely the bug this replaces.
 */
function waitForControllerChange(timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    if (!('serviceWorker' in navigator)) {
      resolve();
      return;
    }

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      navigator.serviceWorker.removeEventListener('controllerchange', finish);
      resolve();
    };

    const timer = setTimeout(finish, timeoutMs);
    navigator.serviceWorker.addEventListener('controllerchange', finish);
  });
}

/**
 * Service-worker update prompt.
 *
 * The app is registered with `registerType: 'prompt'`, so a new bundle waits
 * instead of swapping itself in. A storekeeper midway through a receipt
 * document should not have the page reloaded under them; they choose when to
 * take the update.
 *
 * "Update" always ends in a reload, and that is deliberate. There are three
 * states a waiting update can be in and only one of them is the textbook one:
 *
 *   1. a worker is waiting for this page to close — it is asked to skip
 *      waiting, then the reload picks up the new bundle;
 *   2. the worker already activated on its own, because this page held no
 *      controller to wait for. Nothing is left to skip, and only the reload
 *      can move the page onto the new bundle;
 *   3. the worker was classified as external because it appeared long after
 *      registration.
 *
 * In cases 2 and 3 the old code sent `SKIP_WAITING` to a registration with no
 * waiting worker, heard nothing back, and left the banner on screen forever.
 * Reloading unconditionally removes that dead end: if the new bundle is in the
 * cache the reload lands on it, and if it somehow is not, the next load says
 * so honestly instead of pretending to have updated.
 */
export function UpdatePrompt() {
  const { t } = useTranslation();
  const [applying, setApplying] = useState(false);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisterError(error) {
      // A failed registration must not break the app — it simply means no
      // offline shell this session.
      console.warn('[pwa] service worker registration failed', error);
    },
  });

  const applyUpdate = useCallback(async () => {
    setApplying(true);

    // Recorded before the reload so the next bundle can prove it is a
    // different one. See `update-flow.ts`.
    beginPendingUpdate(BUILD_ID);

    try {
      const registration = await navigator.serviceWorker?.getRegistration();
      const waiting = registration?.waiting ?? null;

      if (waiting) {
        // fire-and-forget: the message either activates the worker or the
        // registration was already past this point.
        void updateServiceWorker(false).catch(() => {});
        await waitForControllerChange(4000);
      }
    } catch {
      // Any failure here still ends in the reload below; there is nothing
      // useful left to tell the user while the page is about to be replaced.
    }

    window.location.reload();
  }, [updateServiceWorker]);

  if (!needRefresh) {
    return null;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="bg-popover text-popover-foreground animate-toast-in fixed inset-x-4 bottom-24 z-[90] flex flex-col gap-3 rounded-lg border p-3 shadow-lg sm:inset-x-auto sm:right-4 sm:bottom-4 sm:max-w-sm"
    >
      <div>
        <p className="text-sm font-medium">{t('pwa.updateTitle')}</p>
        <p className="text-muted-foreground mt-0.5 text-xs">
          {applying ? t('pwa.applying') : t('pwa.updateBody')}
        </p>
      </div>

      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={applying}
          onClick={() => setNeedRefresh(false)}
        >
          {t('pwa.later')}
        </Button>
        <Button size="sm" disabled={applying} onClick={() => void applyUpdate()}>
          {applying ? <Spinner /> : <RefreshCw />}
          {applying ? t('pwa.applyingAction') : t('pwa.updateNow')}
        </Button>
      </div>
    </div>
  );
}
